import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  educationCodeOptions,
  educationCodeOptions02,
  educationDetailInputList,
  relationOptions,
} from '../../../../common/data';
import TextField from '../../../../components/common/TextField';

import Typography from '../../../../components/common/Typography';
import DetailButtonSection from '../../../../components/DetailButtonSection';
import { SelectBox } from '../../../../components/common/SelectBox';
import TotalSection from '../../../../components/TotalSection';
import {
  formatNumber,
  getAlertMessageFunction,
  getTotal,
  handleNumberChange2,
  magamCertifyCheck,
} from '../../../../common/Util';
import SlideRightDialog from '../../../../components/common/SlideRightDialog';
import { getEducationDetail, updateEducationInfo } from '../educationSlice';
import { getFilteredEducationCodes, isValidate } from '../util';
import { setIsDataModified } from '../../../common/commonSlice';

/*
  이름 : v.htm038
  관계 : familyCode[v.htm002]
  주민등록번호 : v.htm001

  장애코드 : v.htm004
  교육비코드 : v.htm015
  교복비 국세청 : v.htm072
  현장학습비 국세청 : v.htm110
  교육비(일반) 국세청 : v.htm023
  교복비 국세청외 : v.htm073
  현장학습비 국세청외 : v.htm111
  교육비(일반) 국세청외 : v.htm024
  공제제외금액 : htm151
*/

const EducationDetail = ({ isOpen, onClose }) => {
  const { t } = useTranslation('deductions/education');
  const { t: tDependents } = useTranslation('dependents');
  const { t: tCommon } = useTranslation('common');

  const inputList = educationDetailInputList(t);
  const educationCodeList = educationCodeOptions(t);
  const educationCodeList02 = educationCodeOptions02(t);
  const relationList = relationOptions(tDependents);

  const dispatch = useDispatch();
  //교육비코드
  const htm015Ref = useRef(null);

  const [data, setData] = useState({});
  const { educationDetail, selectedEducationDetail } = useSelector(state => state.education);
  const { isMagam, isCertify } = useSelector(state => state.common);

  useEffect(() => {
    if (selectedEducationDetail?.htm001) {
      dispatch(getEducationDetail(selectedEducationDetail));
    } else {
      onClose();
    }
  }, [selectedEducationDetail]);

  useEffect(() => {
    setData({
      ...educationDetail,
      total: getTotal(
        [educationDetail],
        ['htm023', 'htm072', 'htm024', 'htm073', 'htm110', 'htm111'],
      ),
    });
  }, [educationDetail]);

  const handleSave = async () => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    const alertMessage = getAlertMessageFunction();
    let formData = {
      htm001: data.htm001,
      htm015: data.htm015,
      htm023: data.htm023,
      htm024: data.htm024,
      htm072: data.htm072,
      htm073: data.htm073,
      htm110: data.htm110,
      htm111: data.htm111,
      htm151: data.htm151,
    };

    let { result, flag, message } = isValidate(formData, t);
    if (!result) {
      let _data = { ...data };

      if (flag == 1) {
        _data.htm072 = '0';
        _data.htm073 = '0';
      } else if (flag == 2) {
        if (Number(data.htm072) > 500000) {
          _data.htm072 = '500000';
          _data.htm073 = '0';
        } else {
          let htm073 = 500000 - Number(data.htm072);
          _data.htm073 = htm073;
        }
      } else if (flag == 3) {
        if (Number(data.htm110) > 300000) {
          _data.htm110 = '300000';
          _data.htm111 = '0';
        } else {
          let htm111 = 300000 - Number(data.htm110);
          _data.htm111 = htm111;
        }
      } else if (flag == 4) {
        _data.htm110 = '0';
        _data.htm111 = '0';
      }

      if (flag == 0 || flag == 2 || flag == 4) {
        htm015Ref.current.focus();
      }

      setData(_data);
      alertMessage(message);
      return;
    }

    console.log('formData >>', formData);
    try {
      const resultAction = await dispatch(updateEducationInfo({ formData: [formData] })).unwrap();

      if (resultAction.resultCode === 'OK') {
        alertMessage(tCommon('alertMessage.saveSuccess'), 'success');
        onClose();
      } else {
        alertMessage(tCommon('alertMessage.saveFail'), 'error');
      }
    } catch (error) {
      alertMessage(tCommon('alertMessage.saveFail'), 'error');
      console.error('저장 중 오류 발생: ', error);
    }
  };

  const codeList = () => {
    let _codeList = educationCodeList;
    if (data.htm002 != '0') {
      if (data.htm004 == '1') {
        _codeList = educationCodeList02;
      } else {
        //장애인 아니면 장애특수 삭제
        _codeList = educationCodeList02.filter(item => item.value != '5');
      }
    }
    return _codeList
      .filter(
        item =>
          item.value === '' || // 항상 "선택하세요" 옵션 포함
          getFilteredEducationCodes(educationDetail).includes(item.value),
      )
      .map((item, index) => (
        <option value={item.value} key={index}>
          {item.label}
        </option>
      ));
  };

  const handleChange = e => {
    let { name, value } = e.target;
    if (name != 'htm001') {
      value = handleNumberChange2(value);
    }
    dispatch(setIsDataModified(true));
    setData({ ...data, [name]: value });
  };

  const total = useMemo(() => {
    return getTotal([data], ['htm023', 'htm072', 'htm024', 'htm073', 'htm110', 'htm111']);
  }, [data]);

  return (
    <SlideRightDialog isOpen={isOpen} onClose={onClose}>
      <div className="displayFlexColumn" style={{ gap: '24px' }}>
        <div className="displayFlexColumn" style={{ gap: '8px' }}>
          <Typography variant="body2Bold" color="Primary-500">
            {t('detail.title')}
          </Typography>
          <Typography variant="subtitleBold">
            {`${educationDetail?.htm038 || ''} (${
              relationList.find(option => option.value === educationDetail?.htm002)?.label || ''
            })`}
          </Typography>
        </div>
        <div className="displayFlexColumn" style={{ gap: '16px' }}>
          {inputList.map(item => {
            if (item.input === 'selectBox') {
              return (
                <SelectBox
                  value={data[item.key]}
                  onChange={handleChange}
                  name={item.key}
                  key={item.key}
                  selectRef={htm015Ref}>
                  {codeList()}
                </SelectBox>
              );
            }
            return (
              <TextField
                label={item.label}
                key={item.key}
                size="small"
                color={item.color}
                variant={item.variant}
                value={formatNumber(data[item.key])}
                onChange={handleChange}
                name={item.key}
                disabled={item.isDisabled}
                inputStyle={{
                  backgroundColor: item.color == 'secondary' ? 'var(--Primary-100)' : '',
                }}
              />
            );
          })}
        </div>
        <TotalSection total={total} />
        <DetailButtonSection onSave={handleSave} onClose={onClose} />
      </div>
    </SlideRightDialog>
  );
};

export default EducationDetail;
